import { FC } from "react";
import { useTheme } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { ChartLegend } from "../../../shared/ui/chart-legend/ChartLegend";
import styles from "./AnalystRatings.module.scss";
import { useTranslation } from "next-i18next";

const chartSegments = [
    { key: "buy", color: "#16BB6F" },
    { key: "sell", color: "#F25555" },
    { key: "hold", color: "#F19500" },
];

interface IAnalystRatingsProps {
    buy: number;
    sell: number;
    hold: number;
    companyName: string;
    consensus: string;
}

export const AnalystRatings: FC<IAnalystRatingsProps> = ({
    buy,
    sell,
    hold,
    companyName,
    consensus,
}) => {
    const { palette } = useTheme();
    const { t } = useTranslation("common");

    const valuesByKey = { buy, sell, hold };

    const chartData = chartSegments.map(segment => ({
        ...segment,
        value: valuesByKey[segment.key],
        name: t(segment.key),
    }));

    const totalRatings = buy + sell + hold || 0;

    const consensusTitle = consensus ? consensus.split(" ") : ["", ""];

    return (
        <div className={styles.container}>
            <div className={styles.chartWrapper}>
                <div className={styles.chart}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={chartData}
                                dataKey="value"
                                nameKey="name"
                                innerRadius="85%"
                                outerRadius="100%"
                                paddingAngle={4}>
                                {chartData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                        stroke={
                                            entry.key === "buy"
                                                ? entry.color
                                                : palette.mode === "dark"
                                                  ? "#1C1F25"
                                                  : "#fff"
                                        }
                                        strokeWidth={2}
                                        className={styles.cell}
                                    />
                                ))}
                            </Pie>
                            <text
                                x="50%"
                                y={consensusTitle[1] ? "40%" : "45%"}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={16}
                                fontWeight="700"
                                fill="#16BB6F">
                                {consensusTitle[0]}
                            </text>
                            {consensusTitle[1] && (
                                <text
                                    x="50%"
                                    y="51%"
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fontSize={16}
                                    fontWeight="700"
                                    fill="#16BB6F">
                                    {consensusTitle[1]}
                                </text>
                            )}
                            <text
                                x="50%"
                                y="63%"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={10}
                                fill={palette.mode === "dark" ? "#E6ECF1" : "#343841"}>
                                {t("analytics.analystForecasts.analystRatings.totalRatings", {
                                    count: totalRatings,
                                })}
                            </text>
                            {/* <Tooltip formatter={(value, name) => [`${value} Ratings`, name]} /> */}
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <ChartLegend buyValue={buy || 0} sellValue={sell || 0} holdValue={hold || 0} />
            </div>
            <div className={styles.description}>
                {t("analytics.analystForecasts.analystRatings.description", {
                    count: totalRatings,
                    companyName,
                })}
            </div>
        </div>
    );
};
